import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { AnalyticsService } from '@app/core/analytics/analytics.service';
import { AppConfig } from '@app/core/app.config';
import { AnalyticsKeyEnum } from '@app/core/enums/analytics/analytics-key.enum';
import { ActionEnum } from '@app/core/enums/analytics/analytics-value.enum';
import { EntityTypeEnum } from '@app/core/enums/entity-type.enum';
import { PermissionEnum } from '@app/core/enums/permissions.enum';
import { Report } from '@app/core/model/client/report';
import { AnalyticsEvent } from '@app/core/model/entities/analytics/analytics-event';
import { Organization } from '@app/core/model/entities/organization/organization';
import { OrganizationLogoService } from '@app/features/main/views/dashboard/organization-logo.service';
import { MyReportService } from '@app/features/main/views/my-pages/my-report/my-report.service';
import { TranslateService } from '@ngx-translate/core';
import { AccessManager } from '@services/managers/access.manager';
import { AppManager } from '@services/managers/app.manager';
import { SnackbarManager } from '@services/managers/snackbar.manager';
import dayjs, { Dayjs } from 'dayjs';
import duration, { DurationUnitType } from 'dayjs/plugin/duration';
import isoWeek from 'dayjs/plugin/isoWeek';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { combineLatest, EMPTY, merge } from 'rxjs';
import { debounceTime, map, mergeWith, startWith, switchMap } from 'rxjs/operators';

@Component({
  selector: 'my-report',
  templateUrl: './my-report.component.html',
  styleUrls: ['./my-report.component.scss'],
  providers: [MyReportService],
  standalone: false
})
export class MyReportComponent implements OnInit {

  public userReport: Report = null;
  public sendDate: Dayjs;

  public Permission = PermissionEnum;
  public Organization = Organization;

  // Days of week: from 1 to 7
  public daysOfWeek: number[] = Array.from(new Array(7), (x, i) => i + 1);
  // Hours of day: from 0 to 23
  public hours: number[] = Array.from(new Array(24), (x, i) => i);
  // One, two or 4 weeks
  public periodicityWeekList: number[] = [7, 14, 28];
  // Periodicity for checks, lease and contract renew dates: from 1 to 12
  public periodicityMonthList: number[] = Array.from(new Array(12), (x, i) => i + 1);
  public timeUnits: string[] = ['WEEKS', 'MONTHS'];

  protected readonly accessManager = inject(AccessManager);
  protected readonly appConfig = inject(AppConfig);
  protected readonly translate = inject(TranslateService);
  private readonly fb = inject(FormBuilder);
  private readonly appManager = inject(AppManager);
  private readonly analyticsService = inject(AnalyticsService);
  private readonly snackbarManager = inject(SnackbarManager);
  private readonly route = inject(ActivatedRoute);
  private readonly reportService = inject(MyReportService);
  private readonly organizationLogoService = inject(OrganizationLogoService);
  private readonly destroyRef = inject(DestroyRef);

  public reportForm = this.fb.group({
    activateReports: this.fb.control(false),
    dayOfWeek: this.fb.control(null),
    hour: this.fb.control(null),
    periodicity: this.fb.control(null),
    noReportDataError: this.fb.control(false),
    entitiesHistory: this.fb.group({
      onAssetCreated: this.fb.control(false),
      onAssetUpdated: this.fb.control(false),
      onAssetDeleted: this.fb.control(false),
      onSpaceCreated: this.fb.control(false),
      onSpaceUpdated: this.fb.control(false),
      onSpaceDeleted: this.fb.control(false),
      onDocumentCreated: this.fb.control(false),
      onDocumentUpdated: this.fb.control(false),
      onDocumentDeleted: this.fb.control(false),
      onCheckCreated: this.fb.control(false),
      onCheckUpdated: this.fb.control(false),
      onCheckDeleted: this.fb.control(false),
      onCheckExpired: this.fb.control(false),
      checkToRenewInterval: this.fb.control(null),
      checkToRenewUnit: this.fb.control(null),
      checkPeriodicityError: this.fb.control(false),
      checkRenewalEnabled: this.fb.control(false),
      onOccupantCreated: this.fb.control(false),
      onOccupantUpdated: this.fb.control(false),
      onOccupantDeleted: this.fb.control(false),
      onLeaseCreated: this.fb.control(false),
      onLeaseUpdated: this.fb.control(false),
      onLeaseDeleted: this.fb.control(false),
      onLeaseExpired: this.fb.control(false),
      leaseToRenewInterval: this.fb.control(null),
      leaseToRenewUnit: this.fb.control(null),
      leasePeriodicityError: this.fb.control(false),
      leaseRenewalEnabled: this.fb.control(false),
      onContractCreated: this.fb.control(false),
      onContractUpdated: this.fb.control(false),
      onContractDeleted: this.fb.control(false),
      onContractExpired: this.fb.control(false),
      contractToRenewInterval: this.fb.control(null),
      contractToRenewUnit: this.fb.control(null),
      contractPeriodicityError: this.fb.control(false),
      contractRenewalEnabled: this.fb.control(false),
      onWorkCreated: this.fb.control(false),
      onWorkUpdated: this.fb.control(false),
      onWorkDeleted: this.fb.control(false),
      onEquipmentCreated: this.fb.control(false),
      onEquipmentUpdated: this.fb.control(false),
      onEquipmentDeleted: this.fb.control(false),
      onProjectCreated: this.fb.control(false),
      onProjectUpdated: this.fb.control(false),
      onProjectDeleted: this.fb.control(false),
      onInvestmentCreated: this.fb.control(false),
      onInvestmentUpdated: this.fb.control(false),
      onInvestmentDeleted: this.fb.control(false)
    })
  });

  constructor() {
    dayjs.extend(isoWeek);
    dayjs.extend(duration);
    dayjs.extend(utc);
    dayjs.extend(timezone);
  }

  public ngOnInit(): void {
    this.reportForm.controls.activateReports.valueChanges
      .subscribe(activateReport => {
        if (activateReport) {
          this.reportForm.controls.dayOfWeek.setValidators([Validators.required]);
          this.reportForm.controls.hour.setValidators([Validators.required]);
          this.reportForm.controls.periodicity.setValidators([Validators.required]);
        } else {
          this.reportForm.controls.dayOfWeek.setValidators([]);
          this.reportForm.controls.hour.setValidators([]);
          this.reportForm.controls.periodicity.setValidators([]);
        }
      });

    combineLatest([
      this.reportForm.controls.dayOfWeek.valueChanges,
      this.reportForm.controls.hour.valueChanges,
      this.reportForm.controls.periodicity.valueChanges
    ])
      .pipe(
        debounceTime(50),
        map(([selectedDay, selectedHour, selectedPeriodicity]) => {
          return this.calculateNextSendDate(selectedDay, selectedHour, selectedPeriodicity);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((nextSendDate) => {
        this.sendDate = nextSendDate;
      });

    merge(
      this.reportForm.controls.activateReports.valueChanges,
      this.reportForm.controls.entitiesHistory.valueChanges
    )
      .pipe(
        startWith(this.reportForm.controls.activateReports.value),
        debounceTime(50),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        if (!this.reportForm.controls.activateReports.value) {
          this.reportForm.controls.noReportDataError.setValue(false);
        } else {
          this.reportForm.controls.noReportDataError.setValue(
            !Object.values(this.reportForm.controls.entitiesHistory.value)
              .filter(value => typeof value === 'boolean')
              .some(isActivated => isActivated)
          );
        }
      });

    combineLatest([
      this.reportForm.controls.entitiesHistory.controls.checkToRenewInterval.valueChanges,
      this.reportForm.controls.entitiesHistory.controls.checkToRenewUnit.valueChanges
    ])
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(([number, unit]) => {

        if (this.reportForm.controls.entitiesHistory.controls.checkRenewalEnabled.value) {
          const checkRenewalInterval = dayjs.duration(number, unit as DurationUnitType).as('days');

          if (checkRenewalInterval < this.reportForm.controls.periodicity.value) {
            this.reportForm.controls.entitiesHistory.controls.checkPeriodicityError.setValue(true);
          } else {
            this.reportForm.controls.entitiesHistory.controls.checkPeriodicityError.setValue(false);
          }
        }
      });

    combineLatest([
      this.reportForm.controls.entitiesHistory.controls.leaseToRenewInterval.valueChanges,
      this.reportForm.controls.entitiesHistory.controls.leaseToRenewUnit.valueChanges
    ])
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(([number, unit]) => {

        if (this.reportForm.controls.entitiesHistory.controls.leaseRenewalEnabled.value) {
          const leaseRenewalInterval = dayjs.duration(number, unit as DurationUnitType).as('days');

          if (leaseRenewalInterval < this.reportForm.controls.periodicity.value) {
            this.reportForm.controls.entitiesHistory.controls.leasePeriodicityError.setValue(true);
          } else {
            this.reportForm.controls.entitiesHistory.controls.leasePeriodicityError.setValue(false);
          }
        }
      });

    combineLatest([
      this.reportForm.controls.entitiesHistory.controls.contractToRenewInterval.valueChanges,
      this.reportForm.controls.entitiesHistory.controls.contractToRenewUnit.valueChanges
    ])
      .pipe(debounceTime(50), takeUntilDestroyed(this.destroyRef))
      .subscribe(([number, unit]) => {

        if (this.reportForm.controls.entitiesHistory.controls.contractRenewalEnabled.value) {
          const contractRenewalInterval = dayjs.duration(number, unit as DurationUnitType).as('days');

          if (contractRenewalInterval < this.reportForm.controls.periodicity.value) {
            this.reportForm.controls.entitiesHistory.controls.contractPeriodicityError.setValue(true);
          } else {
            this.reportForm.controls.entitiesHistory.controls.contractPeriodicityError.setValue(false);
          }
        }
      });

    this.reportForm.controls.entitiesHistory.controls.checkRenewalEnabled.valueChanges.pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.reportForm.controls.entitiesHistory.controls.checkToRenewInterval.updateValueAndValidity();
    });

    this.reportForm.controls.entitiesHistory.controls.leaseRenewalEnabled.valueChanges.pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.reportForm.controls.entitiesHistory.controls.leaseToRenewInterval.updateValueAndValidity();
    });

    this.reportForm.controls.entitiesHistory.controls.contractRenewalEnabled.valueChanges.pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.reportForm.controls.entitiesHistory.controls.contractToRenewInterval.updateValueAndValidity();
    });

    this.reportForm.controls.periodicity.valueChanges.pipe(
      debounceTime(50),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.reportForm.controls.entitiesHistory.controls.checkToRenewInterval.updateValueAndValidity();
      this.reportForm.controls.entitiesHistory.controls.leaseToRenewInterval.updateValueAndValidity();
      this.reportForm.controls.entitiesHistory.controls.contractToRenewInterval.updateValueAndValidity();
    });

    // Load and get report for the selected organization
    this.route.data.pipe(
      map(data => data['userReportExists']),
      switchMap(exist => {
        if (exist) return this.reportService.getReportByOrganizationId(this.appManager.currentOrganization.id);
        else return this.reportService.createReport(this.appManager.currentOrganization.id, dayjs.tz.guess());
      }),
      // Load organization logo
      mergeWith(this.organizationLogoService.loadOrganizationLogo().pipe(switchMap(() => EMPTY))),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(report => {
      // Set report and reset form
      this.userReport = report;
      this.resetForm();
    });
  }

  /**
   * Saves the new configuration of the user report
   */
  public updateUserReport(): void {
    const variables = {
      reportId: this.userReport.id,
      organizationId: this.appManager.currentOrganization.id,
      reportInput: {
        enabled: this.reportForm.controls.activateReports.value,

        weekday: this.reportForm.controls.dayOfWeek.value,
        timeOfDay: this.reportForm.controls.hour.value,
        frequency: this.reportForm.controls.periodicity.value,
        timezoneId: dayjs.tz.guess(),

        assetConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onAssetCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onAssetUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onAssetDeleted.value
        },

        spaceConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onSpaceCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onSpaceUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onSpaceDeleted.value
        },

        checkConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onCheckCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onCheckUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onCheckDeleted.value,
          expiredEnabled: this.reportForm.controls.entitiesHistory.controls.onCheckExpired.value,
          renewal: {
            enabled: this.reportForm.controls.entitiesHistory.controls.checkRenewalEnabled.value,
            ...(this.reportForm.controls.entitiesHistory.controls.checkRenewalEnabled.value && {
              interval: this.reportForm.controls.entitiesHistory.controls.checkToRenewInterval.value,
              unit: this.reportForm.controls.entitiesHistory.controls.checkToRenewUnit.value
            })
          }
        },

        occupantConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onOccupantCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onOccupantUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onOccupantDeleted.value
        },

        leaseConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onLeaseCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onLeaseUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onLeaseDeleted.value,
          expiredEnabled: this.reportForm.controls.entitiesHistory.controls.onLeaseExpired.value,
          renewal: {
            enabled: this.reportForm.controls.entitiesHistory.controls.leaseRenewalEnabled.value,
            ...(this.reportForm.controls.entitiesHistory.controls.leaseRenewalEnabled.value && {
              interval: this.reportForm.controls.entitiesHistory.controls.leaseToRenewInterval.value,
              unit: this.reportForm.controls.entitiesHistory.controls.leaseToRenewUnit.value
            })
          }
        },

        contractConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onContractCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onContractUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onContractDeleted.value,
          expiredEnabled: this.reportForm.controls.entitiesHistory.controls.onContractExpired.value,
          renewal: {
            enabled: this.reportForm.controls.entitiesHistory.controls.contractRenewalEnabled.value,
            ...(this.reportForm.controls.entitiesHistory.controls.contractRenewalEnabled.value && {
              interval: this.reportForm.controls.entitiesHistory.controls.contractToRenewInterval.value,
              unit: this.reportForm.controls.entitiesHistory.controls.contractToRenewUnit.value
            })
          }
        },

        workConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onWorkCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onWorkUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onWorkDeleted.value
        },

        projectConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onProjectCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onProjectUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onProjectDeleted.value
        },

        equipmentConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onEquipmentCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onEquipmentUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onEquipmentDeleted.value
        },

        investmentConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onInvestmentCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onInvestmentUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onInvestmentDeleted.value
        },

        documentConfig: {
          createdEnabled: this.reportForm.controls.entitiesHistory.controls.onDocumentCreated.value,
          updatedEnabled: this.reportForm.controls.entitiesHistory.controls.onDocumentUpdated.value,
          deletedEnabled: this.reportForm.controls.entitiesHistory.controls.onDocumentDeleted.value
        }
      }
    };
    const analyticsEvent = new AnalyticsEvent(ActionEnum.UPDATE, EntityTypeEnum.USER_REPORT);
    analyticsEvent.addProperties({[AnalyticsKeyEnum.ENTITY_ID]: this.userReport.id.toString()});
    this.reportService.updateUserReport(variables)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: report => {
          this.userReport = report;
          analyticsEvent.addProperties({[AnalyticsKeyEnum.CURRENT_PAGE]: ActionEnum.SAVE});
          this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.EDIT_SAVED'));
        },
        error: () => {
          analyticsEvent.addProperties({[AnalyticsKeyEnum.CURRENT_PAGE]: ActionEnum.CANCEL});
        },
        complete: () => {
          this.analyticsService.trackEvent(analyticsEvent);
        }
      });
  }

  /**
   * Resets the Angular Form based on the current user report entity
   */
  public resetForm(): void {
    this.reportForm.controls.activateReports.setValue(this.userReport.enabled);
    this.reportForm.controls.dayOfWeek.setValue(this.userReport.weekday);
    this.reportForm.controls.hour.setValue(this.userReport.timeOfDay);
    this.reportForm.controls.periodicity.setValue(this.userReport.frequency);

    if (this.userReport.assetConfig) {
      this.reportForm.controls.entitiesHistory.controls.onAssetCreated.setValue(!!this.userReport.assetConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onAssetUpdated.setValue(!!this.userReport.assetConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onAssetDeleted.setValue(!!this.userReport.assetConfig.deletedEnabled);
    }

    if (this.userReport.spaceConfig) {
      this.reportForm.controls.entitiesHistory.controls.onSpaceCreated.setValue(!!this.userReport.spaceConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onSpaceUpdated.setValue(!!this.userReport.spaceConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onSpaceDeleted.setValue(!!this.userReport.spaceConfig.deletedEnabled);
    }

    if (this.userReport.documentConfig) {
      this.reportForm.controls.entitiesHistory.controls.onDocumentCreated.setValue(!!this.userReport.documentConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onDocumentUpdated.setValue(!!this.userReport.documentConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onDocumentDeleted.setValue(!!this.userReport.documentConfig.deletedEnabled);
    }

    if (this.userReport.checkConfig) {
      this.reportForm.controls.entitiesHistory.controls.onCheckCreated.setValue(!!this.userReport.checkConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onCheckUpdated.setValue(!!this.userReport.checkConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onCheckDeleted.setValue(!!this.userReport.checkConfig.deletedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onCheckExpired.setValue(!!this.userReport.checkConfig.expiredEnabled);
      if (this.userReport.checkConfig.renewal) {
        this.reportForm.controls.entitiesHistory.controls.checkRenewalEnabled.setValue(this.userReport.checkConfig.renewal.enabled);
        if (this.userReport.checkConfig.renewal.enabled) {
          this.reportForm.controls.entitiesHistory.controls.checkToRenewInterval.setValue(this.userReport.checkConfig.renewal.interval);
          this.reportForm.controls.entitiesHistory.controls.checkToRenewUnit.setValue(this.userReport.checkConfig.renewal.unit);
        }
      }
    }

    if (this.userReport.occupantConfig) {
      this.reportForm.controls.entitiesHistory.controls.onOccupantCreated.setValue(!!this.userReport.occupantConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onOccupantUpdated.setValue(!!this.userReport.occupantConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onOccupantDeleted.setValue(!!this.userReport.occupantConfig.deletedEnabled);
    }

    if (this.userReport.leaseConfig) {
      this.reportForm.controls.entitiesHistory.controls.onLeaseCreated.setValue(!!this.userReport.leaseConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onLeaseUpdated.setValue(!!this.userReport.leaseConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onLeaseDeleted.setValue(!!this.userReport.leaseConfig.deletedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onLeaseExpired.setValue(!!this.userReport.leaseConfig.expiredEnabled);
      if (this.userReport.leaseConfig.renewal) {
        this.reportForm.controls.entitiesHistory.controls.leaseRenewalEnabled.setValue(this.userReport.leaseConfig.renewal.enabled);
        if (this.userReport.leaseConfig.renewal.enabled) {
          this.reportForm.controls.entitiesHistory.controls.leaseToRenewInterval.setValue(this.userReport.leaseConfig.renewal.interval);
          this.reportForm.controls.entitiesHistory.controls.leaseToRenewUnit.setValue(this.userReport.leaseConfig.renewal.unit);
        }
      }
    }

    if (this.userReport.contractConfig) {
      this.reportForm.controls.entitiesHistory.controls.onContractCreated.setValue(!!this.userReport.contractConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onContractUpdated.setValue(!!this.userReport.contractConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onContractDeleted.setValue(!!this.userReport.contractConfig.deletedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onContractExpired.setValue(!!this.userReport.contractConfig.expiredEnabled);
      if (this.userReport.contractConfig.renewal) {
        this.reportForm.controls.entitiesHistory.controls.contractRenewalEnabled.setValue(this.userReport.contractConfig.renewal.enabled);
        if (this.userReport.contractConfig.renewal.enabled) {
          this.reportForm.controls.entitiesHistory.controls.contractToRenewInterval.setValue(this.userReport.contractConfig.renewal.interval);
          this.reportForm.controls.entitiesHistory.controls.contractToRenewUnit.setValue(this.userReport.contractConfig.renewal.unit);
        }
      }
    }

    if (this.userReport.workConfig) {
      this.reportForm.controls.entitiesHistory.controls.onWorkCreated.setValue(!!this.userReport.workConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onWorkUpdated.setValue(!!this.userReport.workConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onWorkDeleted.setValue(!!this.userReport.workConfig.deletedEnabled);
    }

    if (this.userReport.equipmentConfig) {
      this.reportForm.controls.entitiesHistory.controls.onEquipmentCreated.setValue(!!this.userReport.equipmentConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onEquipmentUpdated.setValue(!!this.userReport.equipmentConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onEquipmentDeleted.setValue(!!this.userReport.equipmentConfig.deletedEnabled);
    }

    if (this.userReport.projectConfig) {
      this.reportForm.controls.entitiesHistory.controls.onProjectCreated.setValue(!!this.userReport.projectConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onProjectUpdated.setValue(!!this.userReport.projectConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onProjectDeleted.setValue(!!this.userReport.projectConfig.deletedEnabled);
    }

    if (this.userReport.investmentConfig) {
      this.reportForm.controls.entitiesHistory.controls.onInvestmentCreated.setValue(!!this.userReport.investmentConfig.createdEnabled);
      this.reportForm.controls.entitiesHistory.controls.onInvestmentUpdated.setValue(!!this.userReport.investmentConfig.updatedEnabled);
      this.reportForm.controls.entitiesHistory.controls.onInvestmentDeleted.setValue(!!this.userReport.investmentConfig.deletedEnabled);
    }
  }

  /**
   * Sends the last report as it was currently saved
   */
  public sendLastReport(): void {
    const analyticsEvent = new AnalyticsEvent(ActionEnum.SEND, EntityTypeEnum.USER_REPORT);
    analyticsEvent.addProperties({[AnalyticsKeyEnum.ENTITY_ID]: this.userReport.id.toString()});
    this.reportService.sendLastReport(this.appManager.currentOrganization.id).subscribe((response) => {
      if (response) {
        analyticsEvent.addProperties({[AnalyticsKeyEnum.CURRENT_PAGE]: ActionEnum.SAVE});
        this.snackbarManager.showActionSnackbar(this.translate.instant('SUCCESS.REPORT_SENT'));
      } else {
        analyticsEvent.addProperties({[AnalyticsKeyEnum.CURRENT_PAGE]: ActionEnum.CANCEL});
        this.snackbarManager.showActionSnackbar(this.translate.instant('ERROR.SENDING_REPORT'));
      }
      this.analyticsService.trackEvent(analyticsEvent);
    });
  }

  /**
   * Resets the report form on cancel
   */
  public onButtonResetClick(): void {
    this.analyticsService.trackEvent(new AnalyticsEvent(ActionEnum.UPDATE, EntityTypeEnum.USER_REPORT)
      .addProperties({[AnalyticsKeyEnum.CURRENT_PAGE]: ActionEnum.CANCEL}));
    this.resetForm();
  }

  /**
   * Format dates
   * @param dateToFormat the date to Format
   */
  public formatDate(dateToFormat: Dayjs): string {
    if (dateToFormat) {
      return dayjs(dateToFormat).format(this.appConfig.DISPLAY_DATE_FORMAT);
    } else {
      return null;
    }
  }


  /**
   * Calculate the next report date. If a previous report exists, the next date will be calculated from it.
   * @param day The week day to send the report
   * @param hour The hour of day to send the report
   * @param frequency The interval in days between each report.
   * @return The next report date.
   */
  private calculateNextSendDate(day: number, hour: number, frequency: number): Dayjs {
    if (this.userReport.lastSendDate.isValid()) {
      let daysToAdd = day - this.userReport.lastSendDate.day();
      if (daysToAdd < 0) daysToAdd += 7;

      const nextDate = this.userReport.lastSendDate
        .add(daysToAdd, 'day')
        .add(frequency, 'day');
      if (nextDate.isAfter(dayjs())) return nextDate;
    }

    if (day < dayjs().day() || (day === dayjs().day() && hour <= dayjs().hour())) {
      return dayjs().day(day + 7);
    }

    return dayjs().day(day);
  }
}
